import { Button } from "@jetblue/kit/button";
import { FieldError } from "@jetblue/kit/field-error";
import { InputCheckbox } from "@jetblue/kit/input-checkbox";
import { InputRoute } from "@jetblue/dotcom-components/input-route";
import { InputText } from "@jetblue/kit/input-text";
import { BestFaresSingleDatePicker } from "../components/best-fares/single-date-picker";

import type { FlightSearchFormLabels } from "../labels";
import type { StatelessFlightSearchFormProperties } from "../stateless";
import type {
  TripType,
  useFlightSearchForm,
} from "../hooks/use-flight-search-form";
import type { PassengerCounts } from "../use-passengers";
import type { useForm } from "../use-form";
import { InputSelect, useSelect } from "@jetblue/kit/input-select";

export type OneWayProperties = {
  bestFareFinderBaseUrl?: ReturnType<
    typeof useFlightSearchForm
  >["bestFareFinderBaseUrl"];
  errors: ReturnType<typeof useFlightSearchForm>["errors"];
  formList: ReturnType<typeof useForm>;
  isOpen?: boolean | undefined;
  onOpenChange?: ((isOpen: boolean) => void) | undefined;
  labels: FlightSearchFormLabels;
  odQuery: StatelessFlightSearchFormProperties["odQuery"];
  onTripTypeChange: (value: TripType | null) => void;
  passengers: PassengerCounts;
  showDatePickerPricing: boolean;
  showPromoCode?: boolean | undefined;
};
export const OneWay = ({
  bestFareFinderBaseUrl,
  errors,
  onTripTypeChange,
  formList: {
    endDate,
    first,
    modify,
    promoCode,
    setPromoCode,
    setUsePoints,
    startDate,
    swapOriginDestination,
    usePoints,
  },
  isOpen,
  onOpenChange,
  labels,
  odQuery,
  passengers,
  showDatePickerPricing,
  showPromoCode,
}: OneWayProperties) => {
  const { departureDate, destination, origin } = first;
  return (
    <div
      className="
        flex flex-col gap-1

        lg:flex-row
      "
    >
      <InputRoute
        className="
          w-full

          lg:w-3/5

          lg:data-[show-promo-code=true]:w-1/2
        "
        data-fs-element="route"
        data-show-promo-code={showPromoCode}
        destination={destination}
        destinationName="to"
        labels={labels}
        odRouteData={{ destination: odQuery.data, origin: odQuery.data }}
        onDestinationChange={(value) => {
          modify(0, "destination", value);
        }}
        onOriginChange={(value) => {
          modify(0, "origin", value);
        }}
        onSwapClick={() => {
          swapOriginDestination(0);
        }}
        origin={origin}
        originName="from"
        showValidation={errors.showValidation}
      />

      <div
        className="
          flex flex-col gap-1

          lg:w-2/5 lg:flex-row

          data-[show-promo-code=true]:lg:w-1/2
        "
        data-show-promo-code={showPromoCode}
      >
        <div className="flex w-full flex-row gap-1" data-fs-element="date">
          <BestFaresSingleDatePicker
            bestFareFinderBaseUrl={bestFareFinderBaseUrl}
            className="w-full"
            destination={destination}
            endDate={endDate}
            error={
              <FieldError>
                {({ isInvalid }: { isInvalid: boolean }) =>
                  isInvalid ? labels.errors.departureDate : ""
                }
              </FieldError>
            }
            headingSlot={
              <InputSelect
                aria-label="trip type"
                {...useSelect({
                  initialSelectedKey: "one-way",
                  onSelectionChange: onTripTypeChange,
                })}
                className="text-label font-extrabold"
                label="Trip Type"
                options={[
                  { label: labels.tripType.roundTrip, value: "round-trip" },
                  { label: labels.tripType.oneWay, value: "one-way" },
                ]}
                value="one-way" // Resets the InputSelect to its initialSelectedKey on select
                variant="simple"
              />
            }
            isOpen={isOpen}
            labels={{
              close: labels.close,
              done: labels.done,
              input: labels.departureDate,
              reset: labels.reset,
            }}
            onOpenChange={onOpenChange}
            origin={origin}
            passengers={passengers}
            selectedDate={departureDate}
            setSelectedDate={(date) => {
              modify(0, "departureDate", date);
            }}
            showDatePickerPricing={showDatePickerPricing}
            showValidation={errors.showValidation}
            startDate={startDate}
            toolbarLabels={labels.datePickerToolbar}
            usePoints={usePoints}
          />
          <input
            className="hidden"
            name="depart"
            readOnly
            value={departureDate?.toString()}
          />
        </div>

        {showPromoCode && (
          <div className="flex w-full flex-row gap-1">
            <InputText
              className="w-full"
              data-fs-element="promo-code"
              label={labels.promoCode}
              labelClassName="truncate"
              name="airPromo"
              onChange={(_, value) => {
                setPromoCode(value);
              }}
              value={promoCode}
            />
          </div>
        )}

        <div
          className="
            mt-2 flex justify-center text-caption

            md:hidden
          "
        >
          <InputCheckbox
            data-fs-element="points-toggle"
            isSelected={usePoints}
            onChange={() => {
              setUsePoints((current) => !current);
            }}
          >
            {labels.loyaltyCheckbox}
          </InputCheckbox>
        </div>

        <div
          className="
            mt-4 flex justify-center

            lg:mt-0
          "
        >
          <Button
            className="
              h-14 w-full whitespace-nowrap

              md:w-fit md:min-w-unset

              lg:rounded-s-none
            "
            data-fs-element="submit"
            size="large"
            type="submit"
          >
            {labels.submit}
          </Button>
        </div>
      </div>
    </div>
  );
};
